import { computed } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';
import type { UpdateReviewBody } from '@shopexperts/shared';

/**
 * Composable for updating reviews as an authenticated client
 * Handles review updates with error and success notifications
 */
export function useUpdateClientReview() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationKey: ['client', 'updateReview'],
    mutationFn: async ({ reviewId, payload }: { reviewId: number; payload: UpdateReviewBody }) => {
      return await api.client.reviewsV2.updateReview({
        params: { review: reviewId },
        body: payload
      });
    },
    onSuccess: () => {
      toaster.create({
        title: 'Review updated successfully!',
        description: 'Your review has been updated.',
        type: 'success',
      });
      // Invalidate reviews queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['client', 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['v2projects', 'review'] });
    },
    onError: (error: any) => {
      console.error('[useUpdateClientReview] Error:', error);
      
      const errorMessage = 
        error?.response?.data?.message || 
        error?.response?.data?.error ||
        error?.message ||
        'Failed to update review. Please try again.';
      
      toaster.create({
        title: 'Error updating review',
        description: errorMessage,
        type: 'error',
      });
    },
  });

  const isUpdating = computed(() => updateMutation.isPending.value);
  const isSuccess = computed(() => updateMutation.isSuccess.value);
  const isError = computed(() => updateMutation.isError.value);

  const updateReview = (reviewId: number, payload: UpdateReviewBody) => {
    return updateMutation.mutateAsync({ reviewId, payload });
  };

  const reset = () => {
    updateMutation.reset();
  };

  return {
    updateMutation,
    isUpdating,
    isSuccess,
    isError,
    updateReview,
    reset,
  };
}

